import { Component, inject, input, OnInit, signal } from '@angular/core';
import { DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { finalize, startWith, Subject, switchMap } from 'rxjs';

// FontAwesome
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faClipboard, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

// Components
import { NavbarComponent } from "../../../template/components/navbar/navbar.component";
import { LoadingButtonComponent } from "../../../shared/components/loading-button/loading-button.component";

// Models
import { LicenseDetails } from '../../models/license-details.model';

// Services
import { ToastService } from '../../../shared/services/toast.service';
import { LicenseService } from '../../sevices/license.service';

enum LicenseStatus {
  IDLE = 'Inizializzata',
  ACTIVE = 'Attiva',
  REACTIVATED = 'Riattivata',
  TERMINATED = 'Terminata',
  SUSPENDED = 'Sospesa'
}

@Component({
  selector: 'app-license-detail',
  imports: [
    DecimalPipe,
    DatePipe,
    NavbarComponent,
    NgClass,
    FontAwesomeModule,
    LoadingButtonComponent
  ],
  templateUrl: './license-detail.component.html'
})
export class LicenseDetailComponent {
  readonly licenseDetails = input.required<LicenseDetails>();
  private readonly licenseSrv = inject(LicenseService);
  private readonly toastSrv = inject(ToastService);

  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faClipboard = faClipboard;

  navList = [{ label: 'Dettagli Licenza', routePath: '/license' }];

  private reloadApiKey$ = new Subject<void>();

  apiKeyStatus = toSignal(
    this.reloadApiKey$.pipe(
      startWith(void 0), // prima chiamata automatica
      switchMap(() => this.licenseSrv.getApiKeyEnabled())
    ),
    { initialValue: { enabled: false } }
  );

  showApiKey = false;
  showClientID = false;
  showRecaptchaKey = false;

  loading = signal(false);

  getLicenseStatus(status: string): string {
    return LicenseStatus[status as keyof typeof LicenseStatus] || LicenseStatus.IDLE;
  }

  getLicenseStatusColor(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'text-bg-success';
      case 'REACTIVATED':
        return 'text-bg-warning';
      case 'TERMINATED':
        return 'text-bg-danger';
      case 'SUSPENDED':
        return 'text-bg-secondary';
      default:
        return 'text-bg-primary';
    }
  }

  toggleApiKeyEnabled() {
    const currentEnabled = this.apiKeyStatus()?.enabled;
    this.loading.set(true);

    this.licenseSrv.setApiKeyEnabled(!currentEnabled)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this.toastSrv.show({
            type: 'success',
            message: `Chiave API ${!currentEnabled ? 'abilitata' : 'disabilitata'} con successo`
          });

          // 👇 ricarica dal backend
          this.reloadApiKey$.next();
        },
        error: (err) => {
          console.error('Errore durante il toggle della chiave API:', err);
          this.toastSrv.show({
            type: 'error',
            message: `Errore durante il ${!currentEnabled ? 'abilitazione' : 'disabilitazione'} della chiave API`
          });
        }
      });
  }

  maskKey(value: string, visible = 4): string {
    if (!value) return '';
    return '•'.repeat(Math.max(0, value.length - visible)) + value.slice(-visible);
  }

  copyKey(value: string) {
    navigator.clipboard.writeText(value).then(() => {
      this.toastSrv.show({
        type: 'success',
        message: 'Chiave copiata negli appunti'
      });
    }).catch(() => {
      this.toastSrv.show({
        type: 'error',
        message: 'Errore durante la copia della chiave'
      });
    });
  }

}
